import { useNavigate } from '@tanstack/react-router';
import { observer } from 'mobx-react-lite';
import {
  type MouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { Icons } from '@/assets/icons/icons';
import { MainLayoutHeaderTunnel } from '@/components/blocks/MainLayout/main-layout-tunnel';
import { Text, Tooltip } from '@/components/ui';
import { Empty } from '@/components/ui/EmptyState';
import { Grid, XStack, YStack } from '@/components/ui/layout/layout';
import { Skeleton } from '@/components/ui/skeleton';
import { Page } from '@/design-system-v2/page';
import {
  FEATURES,
  featureFlagsReady,
  hasFeature,
} from '@/features/feature-flags';
import { UpgradeBanner } from '@/features/subscription/upgrade-banner';
import { isNewTabClick, useTabs } from '@/features/tabs';
import { useHydratedFiles, type HydratedFile } from '@/hooks/useHydratedFile';
import { logError } from '@/lib/errorLogging';
import { cn } from '@/lib/utils';
import { trackEvent } from '@/utils/analytics';

import {
  SPRITE_CREATE_DESCRIPTION,
  SpriteCreateOptions,
} from './sprite-create';
import {
  spriteSourceLabel,
  spriteTypeLabel,
  SPRITE_TYPE_FILTER_OPTIONS,
} from './sprite-presentation';
import { SpriteActionsMenu } from './SpriteActionsMenu';
import { SpriteListRow } from './SpriteListRow';
import { SpritePaperThumb } from './SpritePaperThumb';
import { type SpriteSummary, spritesStore } from './sprites.store';
import { useSpriteItem } from './use-sprite-item';

/**
 * Shown to everyone. Without the sprites flag (e.g. after a downgrade) the Library is view-only:
 * saved files still open, and an upgrade prompt replaces the create options.
 */
export const AllSprites = observer(() => {
  const canCreate = hasFeature(FEATURES.SPRITES);

  // Refetch every time the page opens so artifacts created elsewhere (e.g. auto-saved from a chat)
  // show up without an app reload. The existing list stays rendered while the refetch runs, so
  // there's no skeleton flash on revisits. The view event also waits for flags: on a cold start the
  // list can load before PostHog has, and `can_create` would read false for a paying user.
  useEffect(() => {
    Promise.all([spritesStore.refresh(), featureFlagsReady()])
      .then(() => {
        trackEvent('SPRITE_TAB_VIEWED', {
          sprite_count: spritesStore.sprites.length,
          can_create: hasFeature(FEATURES.SPRITES),
        });
      })
      .catch((error: unknown) => {
        logError(error, 'Failed to load sprites', { context: 'AllSprites' });
      });
  }, []);

  const showUpgradeBanner = !canCreate && spritesStore.sprites.length > 0;

  return (
    <Page.Body>
      <MainLayoutHeaderTunnel.In>
        <SpritesPageTitle />
      </MainLayoutHeaderTunnel.In>
      {showUpgradeBanner && <SpritesUpgradeBanner />}
      <SpritesContent canCreate={canCreate} />
    </Page.Body>
  );
});

const SpritesPageTitle = observer(() => {
  const [type, ...rest] = spritesStore.typeFilters;
  const label =
    type && rest.length === 0
      ? (SPRITE_TYPE_FILTER_OPTIONS.find((option) => option.value === type)
          ?.label ?? 'All files')
      : 'All files';
  return <Page.Title>{label}</Page.Title>;
});

const SPRITE_GRID = 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4';

const SPRITE_CARD_WELL =
  'relative aspect-[2/1] overflow-hidden rounded-md bg-surface-active';

const NO_FILE: HydratedFile = { status: 'none', file: undefined };

const SpritesContent = observer(({ canCreate }: { canCreate: boolean }) => {
  const navigate = useNavigate();
  const { openTab } = useTabs();
  const { visibleSprites, viewMode } = spritesStore;
  const fileIds = spritesStore.sprites.flatMap((sprite) =>
    sprite.file_id ? [sprite.file_id] : []
  );
  const hydrated = useHydratedFiles(fileIds);

  const openSprite = useCallback(
    (sprite: SpriteSummary, event?: MouseEvent) => {
      if (event && isNewTabClick(event)) {
        openTab({ path: `/sprites/${sprite.id}` });
        return;
      }
      navigate({ to: '/sprites/$spriteId', params: { spriteId: sprite.id } });
    },
    [navigate, openTab]
  );

  if (!spritesStore.isLoaded) return <SpritesLoading />;
  if (spritesStore.sprites.length === 0)
    return canCreate ? <SpritesEmpty /> : <SpritesUpgradeEmpty />;
  if (visibleSprites.length === 0) return <SpritesNoMatches />;

  const hydratedById = new Map(
    fileIds.map((id, index) => [id, hydrated[index]])
  );
  const hydratedFor = (sprite: SpriteSummary): HydratedFile =>
    (sprite.file_id && hydratedById.get(sprite.file_id)) || NO_FILE;

  if (viewMode === 'list') {
    return (
      <YStack className="gap-1">
        {visibleSprites.map((sprite) => (
          <SpriteListRow
            key={sprite.id}
            sprite={sprite}
            hydrated={hydratedFor(sprite)}
            onOpen={openSprite}
          />
        ))}
      </YStack>
    );
  }

  return (
    <Grid className={SPRITE_GRID}>
      {visibleSprites.map((sprite) => (
        <SpriteCard
          key={sprite.id}
          sprite={sprite}
          hydrated={hydratedFor(sprite)}
          onOpen={openSprite}
        />
      ))}
    </Grid>
  );
});

const SKELETON_COUNT = 8;

const SpritesLoading = observer(() =>
  spritesStore.viewMode === 'list' ? (
    <YStack className="gap-1">
      {[...Array(SKELETON_COUNT)].map((_, index) => (
        <XStack key={index} className="items-center gap-4 p-2">
          <Skeleton className="size-12 shrink-0 rounded-xl" />
          <YStack className="flex-1 min-w-0 gap-1.5">
            <Skeleton className="h-4 w-2/5 rounded" />
            <Skeleton className="h-3 w-1/4 rounded" />
          </YStack>
          <Skeleton className="h-3 w-12 shrink-0 rounded" />
        </XStack>
      ))}
    </YStack>
  ) : (
    <Grid className={SPRITE_GRID}>
      {[...Array(SKELETON_COUNT)].map((_, index) => (
        <YStack
          key={index}
          className="overflow-hidden rounded-xl border border-border bg-surface"
        >
          <div className="p-1.5 pb-0">
            <div className={SPRITE_CARD_WELL} />
          </div>
          <YStack className="gap-0.5 px-3 py-2.5">
            <XStack className="h-5 items-center">
              <Skeleton className="h-3.5 w-3/5 rounded" />
            </XStack>
            <XStack className="h-4 items-center">
              <Skeleton className="h-3 w-2/5 rounded" />
            </XStack>
          </YStack>
        </YStack>
      ))}
    </Grid>
  )
);

const SpritesEmpty = () => (
  <Empty.Root variant="action">
    <Empty.Header>
      <Empty.Title>Create your first file</Empty.Title>
      <Empty.Description>{SPRITE_CREATE_DESCRIPTION}</Empty.Description>
    </Empty.Header>
    <Empty.Content className="max-w-md">
      <SpriteCreateOptions />
    </Empty.Content>
  </Empty.Root>
);

const SpritesUpgradeEmpty = () => (
  <Empty.Root variant="action">
    <Empty.Header>
      <Empty.Title>Upgrade to create files</Empty.Title>
      <Empty.Description>{SPRITE_CREATE_DESCRIPTION}</Empty.Description>
    </Empty.Header>
    <Empty.Content>
      <UpgradeBanner.UpgradeButton source="library_empty">
        Upgrade
      </UpgradeBanner.UpgradeButton>
    </Empty.Content>
  </Empty.Root>
);

const SpritesUpgradeBanner = () => (
  <UpgradeBanner.Root>
    <UpgradeBanner.Content>
      <UpgradeBanner.Title>Upgrade to create new files</UpgradeBanner.Title>
      <UpgradeBanner.Description>
        You can still open the files you made. Upgrade to have Littlebird build
        websites, PDFs and more.
      </UpgradeBanner.Description>
    </UpgradeBanner.Content>
    <UpgradeBanner.UpgradeButton source="library_view_only">
      Upgrade
    </UpgradeBanner.UpgradeButton>
  </UpgradeBanner.Root>
);

const SpritesNoMatches = () => (
  <Empty.Root>
    <Empty.Media variant="icon">
      <Icons.search />
    </Empty.Media>
    <Empty.Header>
      <Empty.Title>No files found</Empty.Title>
      <Empty.Description>
        Try adjusting your search or filters.
      </Empty.Description>
    </Empty.Header>
  </Empty.Root>
);

interface SpriteCardProps {
  sprite: SpriteSummary;
  hydrated: HydratedFile;
  onOpen: (sprite: SpriteSummary, event?: MouseEvent) => void;
}

const SpriteCard = observer(({ sprite, hydrated, onOpen }: SpriteCardProps) => {
  const {
    artifact,
    title,
    KindIcon,
    isMenuOpen,
    setIsMenuOpen,
    interactiveProps,
  } = useSpriteItem(sprite, hydrated, onOpen);
  const typeLabel = spriteTypeLabel(sprite);
  const titleRef = useRef<HTMLParagraphElement>(null);
  const [isTitleTooltipOpen, setIsTitleTooltipOpen] = useState(false);
  const updateTitleTooltip = (open: boolean) => {
    const titleElement = titleRef.current;
    setIsTitleTooltipOpen(
      open &&
        !!titleElement &&
        titleElement.scrollWidth > titleElement.clientWidth
    );
  };

  return (
    <YStack
      {...interactiveProps}
      onFocus={(event) => {
        if (
          event.target === event.currentTarget &&
          event.currentTarget.matches(':focus-visible')
        )
          updateTitleTooltip(true);
      }}
      onBlur={(event) => {
        if (event.target === event.currentTarget) updateTitleTooltip(false);
      }}
      className={cn(
        'group relative cursor-default overflow-hidden',
        'rounded-xl border border-border bg-surface hover:bg-surface-hover',
        'focus-ring-inset transition-colors',
        isMenuOpen && 'bg-surface-hover'
      )}
    >
      <div className="relative p-1.5 pb-0">
        {/* Inner radius is the card radius minus this inset, so the corners stay concentric. */}
        <div
          className={cn(
            SPRITE_CARD_WELL,
            artifact.display.kind === 'icon' && 'dark:bg-background-subtle'
          )}
        >
          <SpritePaperThumb
            artifact={artifact}
            fallbackIcon={KindIcon}
            fallbackLabel={typeLabel}
          />
        </div>
        <SpriteActionsMenu
          sprite={sprite}
          file={hydrated.file}
          onOpen={() => onOpen(sprite)}
          isMenuOpen={isMenuOpen}
          onMenuOpenChange={setIsMenuOpen}
          className="absolute right-2 top-2 z-[3]"
        />
      </div>
      <YStack className="gap-0.5 px-3 py-2.5 min-w-0">
        <Tooltip
          label={title}
          open={isTitleTooltipOpen}
          onOpenChange={updateTitleTooltip}
        >
          <Text ref={titleRef} className="font-medium truncate text-sm">
            {title}
          </Text>
        </Tooltip>
        <Text className="text-xs text-foreground-muted truncate">
          {spriteSourceLabel(sprite) ?? typeLabel}
        </Text>
      </YStack>
    </YStack>
  );
});
